import { FC, useState } from 'react';
import Button from '@components/common/button';

import { useToast } from '@components/common/toast';
import Icon from '@components/common/assets';
import AccLightContinueModal from '@components/common/modals/accLightModal/contents/continue';
import Text from '@components/common/text';
import {
  useAdditionalItems,
  useApplicationPositionAccStatus,
  useApplyPosition,
  useRequiredItems,
} from '@repository/match';
import { JDModalType } from '@components/position/jd/apply/modals';
import { useJD } from '@repository/position/usePosition';
import ResponsiveModal from '@components/_atoms/ResponsiveModal';
import ProfileCard from './profileCard';
import { isAdditionValid } from '../additionalInputModal/convertUtil';
import { AccLightStatusTypeEnum } from '@domain/rs/match/PositionAccStatusRs';
import Colors from '@domain/constant/colors';

import { FontStyle } from '@domain/constant/fontStyle';
import classnames from 'classnames/bind';
import styles from './index.module.scss';
const cx = classnames.bind(styles);

interface ApplyModalProps {
  visible: boolean;
  onClose: () => void;
  setModalState: (modalState: JDModalType) => void;
  positionSn: number;
}

const ApplyModal = ({ visible, onClose, setModalState, positionSn }: ApplyModalProps) => {
  const [visibleAccLightContinueModal, setVisibleAccLightContinueModal] = useState(false);
  const Toast = useToast();
  const { data: profileItems } = useRequiredItems(positionSn);
  const { data: additionalItems } = useAdditionalItems(positionSn);
  const { data: additionalAccLight } = useApplicationPositionAccStatus(positionSn);
  const { refetch } = useJD(positionSn);
  const isAdditionalExist = !!additionalItems?.isAdditionalExist;
  const isAdditionalAccLight = !!additionalAccLight?.accApplyRequiredYn;

  const { mutateAsync: applyPositionAsync, isLoading } = useApplyPosition({
    onSuccess: () => {
      refetch();
      setModalState('APPLY_SUCCESS');
    },
    onError: (err) => {
      let content: string;

      switch (err?.response?.data?.errorCode) {
        case 'B902':
          content = '포지션이 삭제되었습니다.';
          break;
        case 'B903':
          content = '포지션이 종료되었습니다.';
          break;
        case 'B904':
          content = '포지션이 마감되었습니다.';
          break;
        case 'B905':
          content = '이미 지원한 포지션 입니다.';
          break;
        case 'B906':
          content = '이미 채용 전형이 진행중인 포지션입니다.';
          break;
        case 'B907': //지원 가능한 횟수를 초과하였습니다.
          setModalState('APPLY_EXCEED');
          return;
        case 'B908':
          Toast({
            type: 'error',
            iconType: 'info',
            content: '지원 조건이 변경되었습니다. 다시 지원해 주세요.',
          });
          onClose();
          return;
        case 'B909':
          setModalState('CONTRACT_END');
          return;
        case 'B002': // 데이터를 찾을 수 없습니다.
        case 'B901': //통신 오류
        default:
          setModalState('ERROR');
          return;
      }

      Toast({
        type: 'error',
        iconType: 'info',
        content,
      });
      onClose();
    },
  });

  const handleApply = async () => {
    if (!additionalItems) return;
    const rq = {
      positionSn,
      additionInfo: additionalItems.additionInfo.map((item) => {
        return { ...item.answer, positionAdditionInfoSn: item.additionInfoSn };
      }),
    };

    await applyPositionAsync(rq as any);
  };

  const getApplyDisabled = () => {
    const isAccLightIncomplete = isAdditionalAccLight
      ? additionalAccLight?.accLiteStatus !== AccLightStatusTypeEnum.COMPLETED
      : false;

    const isAdditionalItem = !isAdditionalExist ? false : !isAdditionValid(additionalItems);

    const isIncomplete = isAdditionalItem || isAccLightIncomplete || !profileItems?.isProfileComplete;

    return isIncomplete;
  };

  return (
    <ResponsiveModal className={cx('modalWrapper')} visible={visible} onClickBg={onClose} dimmed={false}>
      <section className={cx('profileApplyModal')}>
        <a className={cx('btnClose')} role="button" onClick={onClose}>
          <Icon name="closeLight" width={32} height={32} />
        </a>

        <h2 className={cx('title')}>지원하기</h2>

        <Text fontStyle={FontStyle.B2_M} color={Colors.C_COOL_GRAY_70} styles={{ marginTop: 16 }}>
          포지션마다 요구하는 프로필 항목이 다를 수 있어요.
        </Text>

        <div className={cx('cardWrapper')}>
          <ProfileCard positionSn={positionSn} />

          {isAdditionalExist && (
            <button className={cx('profileCard')} onClick={() => setModalState('ADDITIONAL_INPUT')}>
              <div className={cx('cardTitle')}>추가 정보</div>

              <CardStatus
                status={
                  isAdditionValid(additionalItems)
                    ? AccLightStatusTypeEnum.COMPLETED
                    : AccLightStatusTypeEnum.BEFORE_APPLY
                }
              />
            </button>
          )}

          {isAdditionalAccLight && (
            <button
              className={cx('profileCard')}
              onClick={() => {
                additionalAccLight?.accLiteStatus === AccLightStatusTypeEnum.IN_PROGRESS
                  ? setVisibleAccLightContinueModal(true)
                  : setModalState('ADDITIONAL_ACC_LIGHT_SELECT');
              }}
              disabled={additionalAccLight?.accLiteStatus === AccLightStatusTypeEnum.COMPLETED}
            >
              <div
                className={cx('cardTitle', {
                  disabled: additionalAccLight?.accLiteStatus === AccLightStatusTypeEnum.COMPLETED,
                })}
              >
                역량검사
              </div>
              <CardStatus status={additionalAccLight?.accLiteStatus} />
            </button>
          )}
        </div>

        <div className={cx('buttons')}>
          <Button className={cx('button')} onClick={() => setModalState('PREVIEW_PROFILE')} theme="secondary">
            미리보기
          </Button>
          <Button className={cx('button')} onClick={handleApply} disabled={getApplyDisabled()} isLoading={isLoading}>
            지원하기
          </Button>
        </div>
      </section>

      {/* 역검 라이트 이어하기 모달*/}
      <AccLightContinueModal
        positionSn={positionSn}
        visible={visibleAccLightContinueModal}
        onClose={() => setVisibleAccLightContinueModal(false)}
      />
    </ResponsiveModal>
  );
};

export default ApplyModal;

interface CardStatusProps {
  status?: AccLightStatusTypeEnum;
}

export const CardStatus: FC<CardStatusProps> = ({ status }) => {
  switch (status) {
    case AccLightStatusTypeEnum.COMPLETED:
      return <div className={cx('cardStatus', 'complete')}>완료</div>;
    case AccLightStatusTypeEnum.IN_PROGRESS:
      return <div className={cx('cardStatus', 'ing')}>진행중</div>;
    default:
      return <div className={cx('cardStatus')}>미완료</div>;
  }
};
